/* eslint-disable no-mixed-operators */

import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import { REHYDRATE } from 'redux-persist'
// import ILogin from '../model/ILogin';
// import { removeUser } from '../store/authSlice';
import StorageService from '../services/storage.service'
import { IRequestOrderAuth } from '../model/IOrder'
import { isPublicPath } from '../routing/publicPaths'

export const HTTPErrorMessage: Record<number, string> = {
  400: 'Please check that all required fields are filled in and comply with the format.',
  413: 'The files are too large or there are too many files.',
  415: 'The media format of the requested data is not supported by the server',
  500: 'An internal server error has occurred. Please contact your administrator.',
  502: 'It seems that the server is temporarily unavailable. Try again later',
  504: 'The server is not responding. It may be temporarily unavailable.',
} as const

const mutex = new Mutex()

export interface IApiError {
  status: number
  data: {
    code: number
    error: string
  }
}

export const transformResponse = (response: any) => response.content

export const getErrorFromCatch = (err: unknown): string => {
  // if the respond has plane text type
  if ((err as { status: string })?.status == 'FETCH_ERROR') return HTTPErrorMessage[502]
  const httpStatusCode = (err as { status: number })?.status

  if (httpStatusCode === 200) return ''

  return (
    (isFetchBaseQueryError(err) && (err?.data as { error: string })?.error) ||
    (isErrorWithMessage(err) && err?.message) ||
    (err as { data: { message: string } })?.data?.message ||
    (isKnownOriginalStatus(err) && HTTPErrorMessage?.[err.originalStatus]) ||
    'unknown error'
  )
}

// --
// -- Try to serialize fields error
// -- data: { message: [ { field: '', error: ''} ] }
// --
export const serializeFieldErrors = (err: any): Record<string, string> | null => {
  if (!err?.data?.message || !Array.isArray(err.data.message)) return null

  return !err?.data?.message || !Array.isArray(err.data.message)
    ? null
    : err.data.message.reduce(
        (errs: Record<string, string>, err: Record<string, string>) =>
          !err?.field || !err?.error
            ? errs
            : {
                ...(errs ?? {}),
                [err.field]: err?.error,
              },
        null,
      )
}

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_HOST,
  prepareHeaders: (headers) => {
    headers.set('Sec-Fetch-Site', 'same-origin')
    return headers
  },
}) as BaseQueryFn<string | FetchArgs, unknown, IApiError>

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, IApiError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQuery(args, api, extraOptions)

  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()
  // if (result.error && (result.error.status === 401 || result.error.status === 410)) {
  // Public pages (e.g. the warranty offer) are opened from an emailed link by a
  // visitor with no stored order credentials, so the bootstrap order lookup is
  // expected to fail there. Clearing storage or bouncing them to the sign-in
  // page would make those pages unreachable.
  if (
    result.error &&
    (result.error.status === 401 || result.error.status === 404) &&
    !isPublicPath(window.location.pathname)
  ) {
    StorageService.removeItem('orderId')
    StorageService.removeItem('zip')
    StorageService.removeItem('name')

    if (window.location.pathname !== `${process.env.REACT_APP_BASE_URL}ordersignin`)
      window.location.href = `${process.env.REACT_APP_BASE_URL}ordersignin`
  }
  return result as QueryReturnValue<unknown, IApiError, {}>
}

const api = createApi({
  reducerPath: 'motickets',
  baseQuery: baseQueryWithReauth,
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === REHYDRATE && action.payload) {
      return (action?.payload as any)?.[reducerPath]
    }
  },
  endpoints: () => ({}),
  tagTypes: ['Tickets'],
})

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error
}

export function isKnownOriginalStatus(error: unknown): error is { originalStatus: number } {
  return (
    typeof error === 'object' &&
    error != null &&
    'originalStatus' in error &&
    typeof error.originalStatus === 'number'
  )
}

export function getErrorByOriginalCode(originalCode: number): string {
  return HTTPErrorMessage?.[originalCode] || ''
}

export function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error != null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  )
}

export function getOrderAuthParam(): IRequestOrderAuth {
  return {
    orderId: StorageService.getItem('orderId') || ' ',
    zip: StorageService.getItem('zip') || ' ',
    name: StorageService.getItem('name') || ' ',
  }
}

export function paramsWithOrderAuth(params?: Record<string, any>): Record<string, any> {
  return {
    ...(params || {}),
    ...getOrderAuthParam(),
  }
}

export default api
